import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Divider,
  Flex,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import {
  CreditAgreement,
  typeguards,
  Util,
} from '@finley-technologies/finley-types';

import {
  AddQueryInputModal,
  PortfolioCovenantQueryInput,
  PortfolioCovenantQueryInputType,
  queryInputTypeToIcon,
} from './AddQueryInputModal';
import { createAthenaQueryShell } from 'packages/athena/ui/AthenaQueryView';
import { createAthenaRecurringQueryShell } from 'packages/athena/ui/AthenaRecurringQueryView';
import { colors } from 'packages/common-frontend/colors';
import { AddTextButton } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { createPostgresQuery } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/covenantPostgresQuery/PostgresQueryView';
import { WipConfig } from 'packages/creditAgreements/ui/types';

export type PortfolioCovenantEditTab =
  | 'properties'
  | 'inputs'
  | 'covenantValue';

const tabsToLabels: Record<PortfolioCovenantEditTab, string> = {
  properties: 'Properties',
  inputs: 'Inputs',
  covenantValue: 'Covenant Value',
};

const PortfolioCovenantSidebarMenuButton = ({
  tab,
  selectedTab,
  onClick,
}: {
  tab: PortfolioCovenantEditTab;
  selectedTab: PortfolioCovenantEditTab;
  onClick: () => void;
}) => {
  return (
    <Button
      width="100%"
      variant="ghost"
      backgroundColor={tab === selectedTab ? colors.aurora[50] : undefined}
      fontSize="18px"
      fontWeight={600}
      onClick={onClick}
      p="8px 16px"
      justifyContent="flex-start"
    >
      {tabsToLabels[tab]}
    </Button>
  );
};

const CollapsedSidebarMenu = ({
  onSidebarOpen,
}: {
  onSidebarOpen: () => void;
}) => {
  return (
    <Box
      borderWidth="1px"
      borderRadius="8px"
      borderColor={colors.gray[200]}
      onClick={onSidebarOpen}
      w="64px"
      p="24px"
      textAlign="center"
      cursor="pointer"
      position="sticky"
      top="0"
    >
      <Icon icon="chevrons-right" />
    </Box>
  );
};

const SidebarMenuCollapseButton = ({
  onSidebarClose,
}: {
  onSidebarClose: () => void;
}) => {
  return (
    <Box w="100%">
      <Flex justifyContent="flex-end" mb="16px">
        <Box cursor="pointer" onClick={onSidebarClose}>
          <Icon icon="chevrons-left" />
        </Box>
      </Flex>
      <Divider />
    </Box>
  );
};

type PortfolioCovenantsSidebarMenuProps = {
  wipConfig?: WipConfig;
  selectedTab: PortfolioCovenantEditTab;
  setSelectedTab: React.Dispatch<
    React.SetStateAction<PortfolioCovenantEditTab>
  >;
  queryInputs?: PortfolioCovenantQueryInput[];
  selectedQueryInput: PortfolioCovenantQueryInput | null;
  setSelectedQueryInput: React.Dispatch<
    React.SetStateAction<PortfolioCovenantQueryInput | null>
  >;
  portfolioCovenantConfigFullPropertyKey: string;
  portfolioCovenant: CreditAgreement.DomainEntity.PortfolioCovenantProperty;
  sidebarState: {
    isSidebarOpen: boolean;
    onSidebarOpen: () => void;
    onSidebarClose: () => void;
  };
};

export const PortfolioCovenantsSidebarMenu = ({
  wipConfig,
  selectedTab,
  setSelectedTab,
  queryInputs,
  selectedQueryInput,
  setSelectedQueryInput,
  portfolioCovenantConfigFullPropertyKey,
  portfolioCovenant,
  sidebarState,
}: PortfolioCovenantsSidebarMenuProps) => {
  const { isSidebarOpen, onSidebarOpen, onSidebarClose } = sidebarState;

  const {
    onOpen: onAddQueryOpen,
    isOpen: isAddQueryOpen,
    onClose: onAddQueryClose,
  } = useDisclosure();
  const toast = useToast();

  const [isAddLoading, setIsAddLoading] = useState(false);

  const recurringDateConfigFields: string[] = useMemo(() => {
    const globalRecurringDateConfigFields =
      portfolioCovenant._config_?.globalRecurringDateConfigs;

    if (
      globalRecurringDateConfigFields &&
      globalRecurringDateConfigFields.length
    ) {
      return globalRecurringDateConfigFields.map(
        (globalRecurringDateConfig) => globalRecurringDateConfig.field,
      );
    }
    return [];
  }, [portfolioCovenant._config_?.globalRecurringDateConfigs]);

  const addAthenaSingleQuery = useCallback(
    async (wc: WipConfig, queryKeyInput: string): Promise<void> => {
      const basePropertyKey = `${portfolioCovenantConfigFullPropertyKey}._config_.athenaQueries`;

      const queries = getIn(wc.formProps.values, basePropertyKey);
      let queriesCopy: CreditAgreement.Config.AthenaConfig.Query[] = [];

      if (queries && queries.length) {
        finleyAssert(
          typeguards.creditAgreement.isAthenaConfigQueries(queries),
          `Retrieved non-Athena Queries structure for [key:${basePropertyKey}]`,
        );
        queriesCopy = _.cloneDeep(queries);
      }
      queriesCopy.push(
        createAthenaQueryShell(queriesCopy.length, queryKeyInput),
      );

      await wc.saveAndUpdateConfigCreditAgreementWip({
        wipConfig: wc,
        propertyKey: basePropertyKey,
        updatedProperty: queriesCopy,
        setIsSaveLoading: setIsAddLoading,
      });
    },
    [portfolioCovenantConfigFullPropertyKey],
  );

  const addAthenaRecurringQuery = useCallback(
    async (
      wc: WipConfig,
      queryKeyInput: string,
    ): Promise<Util.Maybe<{ errorMessage: string }>> => {
      const basePropertyKey = `${portfolioCovenantConfigFullPropertyKey}._config_.athenaRecurringQueries`;
      const globalRecurringDateConfigFields =
        portfolioCovenant._config_?.globalRecurringDateConfigs;

      // If there are already global recurring date configs set up for the portfolio covenant, we can add the athena recurring query
      // since Athena recurring queries rely on at least one global recurring date config.
      // Otherwise, alert user to simply add a Single Query instead
      if (
        globalRecurringDateConfigFields &&
        globalRecurringDateConfigFields.length
      ) {
        const recurringQueries = getIn(wc.formProps.values, basePropertyKey);
        finleyAssert(
          typeguards.creditAgreement.isAthenaConfigRecurringQueries(
            recurringQueries,
          ),
          `Retrieved non-Athena Recurring Queries structure for [key:${basePropertyKey}]`,
        );
        const recurringQueriesCopy = _.cloneDeep(recurringQueries);

        recurringQueriesCopy.push(
          createAthenaRecurringQueryShell(
            recurringQueriesCopy.length,
            queryKeyInput,
            recurringDateConfigFields,
          ),
        );

        await wc.saveAndUpdateConfigCreditAgreementWip({
          wipConfig: wc,
          propertyKey: basePropertyKey,
          updatedProperty: recurringQueriesCopy,
          setIsSaveLoading: setIsAddLoading,
        });
      } else {
        return {
          errorMessage:
            'At least one existing recurring date configuration is required to add a Multi Query. Otherwise, add a Single Query.',
        };
      }

      return null;
    },
    [
      portfolioCovenantConfigFullPropertyKey,
      portfolioCovenant._config_?.globalRecurringDateConfigs,
      recurringDateConfigFields,
    ],
  );

  const addPostgresQuery = useCallback(
    async ({
      wc,
      queryTypeInput,
      queryKeyInput,
    }: {
      wc: WipConfig;
      queryTypeInput: CreditAgreement.Config.CommonPostgresQuery.Query.QuerySet['type'];
      queryKeyInput: string;
    }): Promise<void> => {
      const basePropertyKey = `${portfolioCovenantConfigFullPropertyKey}._config_.postgresQueries`;

      const postgresQueries = getIn(wc.formProps.values, basePropertyKey);
      const postgresQueriesCopy = _.cloneDeep(postgresQueries);

      postgresQueriesCopy.push(
        createPostgresQuery({
          idx: postgresQueriesCopy.length,
          type: queryTypeInput,
          name: queryKeyInput,
          wipConfig: wc,
        }),
      );

      await wc.saveAndUpdateConfigCreditAgreementWip({
        wipConfig: wc,
        propertyKey: basePropertyKey,
        updatedProperty: postgresQueriesCopy,
        setIsSaveLoading: setIsAddLoading,
      });
    },
    [portfolioCovenantConfigFullPropertyKey],
  );

  const onClickAdd = useCallback(
    async ({
      queryKeyInput,
      queryTypeInput,
    }: {
      queryKeyInput: string;
      queryTypeInput: PortfolioCovenantQueryInputType;
    }): Promise<void> => {
      const errors: string[] = [];
      if (wipConfig) {
        if (queryTypeInput === 'singleQuery') {
          await addAthenaSingleQuery(wipConfig, queryKeyInput);
        } else if (queryTypeInput === 'multiQuery') {
          const athenaRecurringQueryError = await addAthenaRecurringQuery(
            wipConfig,
            queryKeyInput,
          );
          if (athenaRecurringQueryError) {
            errors.push(athenaRecurringQueryError.errorMessage);
          }
        } else if (
          CreditAgreement.Config.CommonPostgresQuery.Query.PostgresQueryTypeIterable.includes(
            queryTypeInput,
          )
        ) {
          await addPostgresQuery({
            wc: wipConfig,
            queryTypeInput,
            queryKeyInput,
          });
        }

        if (errors.length) {
          toast({
            ...errorToastConfig,
            title: 'Add Input Error',
            description: errors.join(', '),
          });

          return;
        }

        setSelectedTab('inputs');
        setSelectedQueryInput({
          queryInputType: queryTypeInput,
          queryInputKey: queryKeyInput,
        });
      }
    },
    [
      addAthenaSingleQuery,
      addAthenaRecurringQuery,
      addPostgresQuery,
      wipConfig,
      setSelectedQueryInput,
      setSelectedTab,
      toast,
    ],
  );

  if (!isSidebarOpen) {
    return <CollapsedSidebarMenu onSidebarOpen={onSidebarOpen} />;
  }

  return (
    <VStack
      padding="24px"
      borderWidth="1px"
      borderRadius="8px"
      borderColor={colors.gray[200]}
      spacing="24px"
      width="300px"
      maxHeight="50vh"
      overflowY="scroll"
      position="sticky"
      top="0"
    >
      <SidebarMenuCollapseButton onSidebarClose={onSidebarClose} />
      {/* Properties menu button */}
      <PortfolioCovenantSidebarMenuButton
        tab="properties"
        selectedTab={selectedTab}
        onClick={() => setSelectedTab('properties')}
      />

      {/* Inputs dropdown menu button */}
      {portfolioCovenant._config_ && (
        <Accordion
          allowToggle
          width="100%"
          padding="0"
          margin="0"
          defaultIndex={selectedTab === 'inputs' ? 0 : undefined}
        >
          <AccordionItem style={{ borderWidth: 0 }}>
            <AccordionButton
              width="100%"
              fontSize="18px"
              fontWeight={600}
              p="8px 16px"
              borderRadius="8px"
            >
              <Flex
                alignItems="center"
                width="100%"
                justifyContent="space-between"
              >
                <Text>Inputs</Text>
                <AccordionIcon />
              </Flex>
            </AccordionButton>
            <AccordionPanel padding="24px 0 0 0">
              {wipConfig?.isMutateAllowed(
                portfolioCovenantConfigFullPropertyKey,
              ) && (
                <Flex justifyContent="right" mb="24px">
                  <AddTextButton
                    ariaLabel="Add query"
                    label="Add Query"
                    onClick={() => onAddQueryOpen()}
                    border="0px"
                  />
                  <AddQueryInputModal
                    wipConfig={wipConfig}
                    isOpen={isAddQueryOpen}
                    onClose={onAddQueryClose}
                    onClickAdd={onClickAdd}
                    initialQueryType="singleQuery"
                    isAddLoading={isAddLoading}
                    mode="add"
                  />
                </Flex>
              )}
              <VStack spacing="16px">
                {_.map(queryInputs, ({ queryInputType, queryInputKey }) => {
                  const isSelectedInput: boolean =
                    selectedTab === 'inputs' &&
                    queryInputType === selectedQueryInput?.queryInputType &&
                    queryInputKey === selectedQueryInput?.queryInputKey;
                  return (
                    <Button
                      key={`${queryInputType}-${queryInputKey}`}
                      width="100%"
                      height="100%"
                      variant="outline"
                      backgroundColor={
                        isSelectedInput ? colors.aurora[50] : undefined
                      }
                      fontWeight={500}
                      lineHeight="24px"
                      onClick={() => {
                        setSelectedTab('inputs');
                        setSelectedQueryInput({
                          queryInputKey,
                          queryInputType,
                        });
                      }}
                      p="8px 16px"
                      borderRadius="8px"
                    >
                      <Flex
                        width="100%"
                        justifyContent="space-between"
                        alignItems="center"
                        color={
                          isSelectedInput ? colors.black[95] : colors.black[70]
                        }
                      >
                        <Tooltip
                          label={queryInputTypeToIcon[queryInputType].tooltip}
                        >
                          <span>
                            <Icon
                              icon={queryInputTypeToIcon[queryInputType].icon}
                            />
                          </span>
                        </Tooltip>
                        <Text
                          width="100%"
                          height="fit-content"
                          textAlign="left"
                          mx="16px"
                          whiteSpace="pre-wrap"
                          maxWidth="140px"
                        >
                          {queryInputKey}
                        </Text>
                        <Icon icon="chevron-right" />
                      </Flex>
                    </Button>
                  );
                })}
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}

      {/* Covenant Value menu button */}
      {portfolioCovenant._config_ && (
        <PortfolioCovenantSidebarMenuButton
          tab="covenantValue"
          selectedTab={selectedTab}
          onClick={() => setSelectedTab('covenantValue')}
        />
      )}
    </VStack>
  );
};

export const getQueryInputsFromWipConfig = (
  covenantKey: string,
  wipConfig?: WipConfig,
): PortfolioCovenantQueryInput[] | undefined => {
  if (!wipConfig) {
    return undefined;
  }

  const portfolioCovenants = getIn(
    wipConfig.formProps.values,
    'portfolioCovenants',
  );
  const portfolioCovenant: CreditAgreement.DomainEntity.PortfolioCovenantProperty = _.find(
    portfolioCovenants,
    (portCov) => portCov.covenantKey === covenantKey,
  );

  if (!portfolioCovenant) {
    return undefined;
  }

  const athenaQueryInputs: PortfolioCovenantQueryInput[] = _.map(
    portfolioCovenant._config_?.athenaQueries,
    (athenaQuery) => {
      return {
        queryInputKey: athenaQuery.query.name,
        queryInputType: 'singleQuery',
      };
    },
  );
  const athenaRecurringQueryInputs: PortfolioCovenantQueryInput[] = _.map(
    portfolioCovenant._config_?.athenaRecurringQueries,
    (athenaRecurringQuery) => {
      return {
        queryInputKey: athenaRecurringQuery.name,
        queryInputType: 'multiQuery',
      };
    },
  );
  const postgresQueryInputs: PortfolioCovenantQueryInput[] = _.map(
    portfolioCovenant._config_?.postgresQueries,
    (pgQuery) => {
      return {
        queryInputKey: pgQuery.name,
        queryInputType: pgQuery.type,
      };
    },
  );

  return [
    ...athenaQueryInputs,
    ...athenaRecurringQueryInputs,
    ...postgresQueryInputs,
  ];
};
